// The Call button in the lead page's communication hub (next to Phone). Opens
// the one dialer pre-filled with the lead (CU-869fmbgfj). Renders nothing,
// label included, unless this user can call from the CRM.
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import Iconify from '../Iconify';
import { useOutbound } from './OutboundCallProvider';
import { useDialer } from './dialerContext';
import { isDiallable } from './outboundModel';
import { phoneProblem } from '../../utils/leadPhone';

// `phoneCheck` is the lead detail's phone_check (CU-869fnwgxf): when the server says the
// stored number can't be dialled, Call stays visible but disabled, and says why.
export default function LeadPageCallButton({ to, leadId, buttonSx, phoneCheck }) {
    const outbound = useOutbound();
    const dialer = useDialer();
    const widget = Boolean(dialer?.canOpen);
    // On a lead's page the button is always there; with no usable number of its own the dialer's
    // server pre-fill works the number out from the lead (CU-869fmc59c).
    const hasLead = leadId !== undefined && leadId !== null && leadId !== '';
    if (!(widget || outbound?.canCall)) return null;
    if (!isDiallable(to) && !(widget && hasLead)) return null;
    const problem = phoneProblem(phoneCheck);
    const deskTitle = !widget && outbound?.desk ? `Call from ${outbound.desk.name || 'your desk'}` : 'Call';
    const title = problem ? `Can't call this number: ${problem}` : deskTitle;
    let label = isDiallable(to) ? `Call ${to}` : 'Call';
    if (problem) label = title;
    const start = () => {
        if (widget) dialer.openDialer({ to: isDiallable(to) ? to : '', leadId: leadId ?? null });
        else outbound.startCall({ to, leadId: leadId ?? null });
    };
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Tooltip title={title}>
                <span>
                    <IconButton
                        onClick={start}
                        disabled={Boolean(problem) || Boolean(widget ? dialer.isCalling : outbound?.busy)}
                        aria-label={label}
                        data-testid="lead-page-call-button"
                        sx={buttonSx}
                    >
                        <Iconify icon="solar:phone-calling-rounded-bold" width={22} height={22} sx={{ color: problem ? 'text.disabled' : 'primary.main' }} />
                    </IconButton>
                </span>
            </Tooltip>
            <Typography sx={{ fontSize: '10px', color: 'grey.500', mt: 0.25 }}>Call</Typography>
        </Box>
    );
}

LeadPageCallButton.propTypes = {
    to: PropTypes.string,
    leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    buttonSx: PropTypes.object,
    phoneCheck: PropTypes.shape({ ok: PropTypes.bool, reason: PropTypes.string, message: PropTypes.string }),
};
